import { h, Component } from 'preact';
import 'store/bolt/pressKitLoader';
import Share from 'components/bolt/icons/share';
import Download from 'components/bolt/icons/download';
import Store from 'store/store';
import 'store/bolt/pressKit';
import s from 'components/bolt/pressKit/pressKit.sss';
import HTML from 'components/core/html/html';
import Footer from 'components/bolt/footer/footer';
import MovieDetails from 'components/bolt/movieDetails/movieDetails';
import { accessNested, joinClasses, sortObjectByOrder } from 'utils/utils';
import { smoothScrollTo } from 'utils/smoothScroll';
import { fade } from 'utils/bolt/transitions';
import { linkOut } from 'utils/bolt/index';
import { navigateInternally } from 'utils/bolt';

export default class PressKit extends Component {

	constructor(props) {
		super(props);

		this.state = {
			activeCategory: 'all'
		};

		let pages = accessNested(this.props, 'store.appData.pages', {});
		this.noHome = !Object.keys(pages).includes('boltHome');
		this.$lazyLoadImages = {};

		this.createIntersectionObserver = this.createIntersectionObserver.bind(this);
		this.updateActiveCategory = this.updateActiveCategory.bind(this);
		this.renderAsset = this.renderAsset.bind(this);
		this.openOverlay = this.openOverlay.bind(this);
		this.onScroll = this.onScroll.bind(this);
		this.onMouseDown = this.onMouseDown.bind(this);
		this.downloadAll = this.downloadAll.bind(this);
	}

	async componentDidMount() {
		this.$scroller.addEventListener('scroll', this.onScroll);
		document.addEventListener('mousedown', this.onMouseDown);

		const hash = window.location.hash.substring(1);
		this.goToHash(hash);
	
		const pageData = this.getPageData();
		Store.emit('TEST_FILE_SHARING', Object.values(pageData.assets)[0]);

		this.resizeObserver = new ResizeObserver(entries => {
			for (let entry of entries) {
				const paddingTop = entry.contentRect.bottom + 8;
				this.setState({ paddingTop });
			}
		});
		
		if (this.$header) {
			this.resizeObserver.observe(this.$header);
		}

		if (!hash) {
			this.createIntersectionObserver();
		}
	}

	componentWillUnmount() {
		this.$scroller.removeEventListener('scroll', this.onScroll);
		document.removeEventListener('mousedown', this.onMouseDown);

		if (this.resizeObserver) {
			this.resizeObserver.unobserve(this.$header);
		}

		if (this.intersectionObserver) {
			Object.values(this.$lazyLoadImages).forEach(image => {
				this.intersectionObserver.unobserve(image);
			});
		}
	}

	createIntersectionObserver() {
		this.intersectionObserver = new IntersectionObserver(entries => {
			for (let entry of entries) {
				if (entry.isIntersecting) {
					const image = Array.from(entry.target.children).find(child => child.nodeName === 'IMG');

					image.onload = () => {
						entry.target.classList.add(s.loaded);
					};
					image.src = image.dataset.src;
					this.intersectionObserver.unobserve(image);
				}
			}
		});

		Object.values(this.$lazyLoadImages).forEach(image => {
			this.intersectionObserver.observe(image);
		});
	}

	onMouseDown(e) {
		const { interactivity } = e;
		const { id, classList } = e.target;

		const sonyFooterOpenEvent = id === 'sonyCreditButton';
		const videoOverlayCloseEvent = accessNested(this.props, 'store.trailerOverlay.overlayOpen', false);
		const pressKitOverlayOpen = this.props.store?.pressKit.overlayOpen;
		const mailchimpModalEvent = classList.contains('mc-closeModal') || classList.contains('mc-layout__modalContent');

		if (!this.props.store.pressKit.show ||
			pressKitOverlayOpen ||
			this.base.contains(e.target) ||
			e.button !== 0 ||
			sonyFooterOpenEvent ||
			this.preventClose ||
			videoOverlayCloseEvent ||
			mailchimpModalEvent) {
			return;
		}
		let closestInteractiveElement = e.target.closest('input, button, a');

		if (!interactivity && !closestInteractiveElement) {
			this.hidePressKit();
		}
	}

	hidePressKit() {
		if (this.noHome) return;

		Store.emit('PRESSKIT_HIDE', true, true);
	}

	async goToHash(hash) {
		if (!hash) {
			return;
		}

		const pageData = this.getPageData();
		const { categories = {}, assets } = pageData;
		const isCategory = categories[hash];
		const isAsset = assets;

		await new Promise(resolve => window.setTimeout(resolve, 200));
		
		if (isCategory) {
			await this.loadAllImagesBefore(hash);
			smoothScrollTo(this.$scroller, document.getElementById('category-' + hash), 600, {
				offset: this.state.paddingTop,
				callback: this.createIntersectionObserver
			});
		} else if (isAsset) {
			this.openOverlay(hash);
			this.createIntersectionObserver();
		}
	}

	async loadAllImagesBefore(category) {
		const sections = Array.from(this.$assets.children);
		const activeCatIndex = sections.findIndex(section => section.id === ('category-' + category));

		const imagesToLoad = [];
		sections.forEach((child, index) => {
			if (index <= activeCatIndex) {
				Array.from(child.getElementsByTagName('img')).forEach(image => {
					imagesToLoad.push(image);
				});
			}
		});
		
		await Promise.all(imagesToLoad.map(image => new Promise(resolve => {
			image.onload = () => {
				image.parentElement.classList.add(s.loaded);
				resolve();
			};
			image.src = image.dataset.src;
		})));
	}

	onScroll() {
		const cats = Array.from(this.$assets.children);
		const scrolled = this.$scroller.scrollTop;
		const nextCategory = [...cats].reverse().findIndex(cat => cat.offsetTop <= (scrolled + 50 + this.state.paddingTop));
		const activeCategory = cats[cats.length - nextCategory - 1]?.id;

		if (!activeCategory) {
			history.replaceState('', document.title, window.location.pathname + window.location.search);
		} else if (window.location.hash.substring(1) !== activeCategory.replace('category-', '')) {
			window.location.hash = activeCategory.replace('category-', '');
		}
	}

	getPageId() {
		let show = this.props.store.pressKit?.show;
		let pageId = typeof show === 'string' && show ? show : this.lastPageId || 'boltPressKit';
		return this.lastPageId = pageId;
	}

	getPageData() {
		return accessNested(this.props, `store.appData.pages.${this.getPageId()}.data`) || {};
	}

	async updateActiveCategory(category) {
		await fade(this.$assets, 'out', 200);
		this.setState({ activeCategory: category });
		this.$scroller.scrollTo(0, 0);
		fade(this.$assets, 'in', 200);
	}

	async downloadImage(data) {
		if (data.assetLink) {
			return linkOut(data.assetLink);
		}
		Store.emit('DOWNLOAD_PRESSKIT_IMAGE', data);
	}

	async shareImage(data) {
		Store.emit('SHARE_PRESSKIT_IMAGE', data);
	}

	async downloadAll() {
		const pageData = this.getPageData();
		const { assets } = pageData;
		let assetList = sortObjectByOrder(assets, (val, key) => ({ ...val, key }));
		let categoryList = [...new Set(assetList.map(asset => asset.category))];
		const hasCategories = categoryList.length > 0;
		const { activeCategory } = this.state;

		if (hasCategories && activeCategory !== 'all') {
			assetList = assetList.filter(asset => asset.category === activeCategory);
		}

		Store.emit('PRESSKIT_DOWNLOAD_ALL', assetList);
	}

	openOverlay(asset) {
		Store.emit('TOGGLE_PRESSKIT_OVERLAY', asset);
	}

	onClick(e) {
		let internalLink = e.target.closest('a[href^="#$"]');
		if (internalLink) {
			e.preventDefault();
			let page = internalLink.getAttribute('href').slice(2).trim();
			let navOptions = {};
			if (page.includes('/?')) {
				let [pageId, qs] = page.split('/?');
				page = pageId;
				navOptions.queryString = '?' + qs;
			}
			navigateInternally(page, navOptions);
		}
	}

	renderHeader() {
		const { activeCategory } = this.state;
		const pageData = this.getPageData();
		const { assets, categories, pageTitle } = pageData;
		const assetList = sortObjectByOrder(assets);
		let categoryList = [...new Set(assetList.map(asset => asset.category))];
		const hasCategories = categoryList.length > 0 && !!categories;
		const activeDownloadableAssets = assetList.filter(asset => (asset.category === activeCategory || activeCategory === 'all') && !asset.assetLink);

		categoryList.unshift('all');

		return (
			<div ref={ref => this.$header = ref} class={s.header}>
				<HTML tag="div" markdown onClickCapture={this.onClick} markdown content={pageTitle} class={joinClasses(s.pageTitle, 'xlargeB')} content={pageTitle} />
				{hasCategories && (
					<div class={s.categoriesNav}>
						{categoryList.map(category => (
							<div
								class={joinClasses(s.category, activeCategory === category && s.active, 'smallB')}
								onClick={() => this.updateActiveCategory(category)}
							>
								{category === 'all' ? (categories[category] || 'All') : (categories[category]) || category}
							</div>
						))}
					</div>
				)}
				{this.renderDownloadAll(activeDownloadableAssets.length === 0)}
			</div>
		);
	}

	renderDownloadAll(disabled) {
		const pageData = this.getPageData();

		const downloadAllCopy = accessNested(this.props, 'store.appData.copy.baseCopy.$DOWNLOAD_ALL');

		if (pageData.downloadAll) {
			return (
				<a class={s.downloadAll} href={pageData.downloadAll} target="_self" download>
					<Download class={s.icon} />
					<div class={joinClasses(s.label, 'large')}>{downloadAllCopy}</div>
				</a>
			);
		}
		return (
			<div class={joinClasses(s.downloadAll, disabled && s.disabled)} onClick={this.downloadAll} target="_self">
				<Download class={s.icon} />
				<div class={joinClasses(s.label, 'large')}>{downloadAllCopy}</div>
			</div>
		);
	}

	renderAssets() {
		const pageData = this.getPageData();
		const { assets, categories } = pageData;
		const { paddingTop } = this.state;
		const assetList = sortObjectByOrder(assets, (val, key) => ({ ...val, key }));
		let categoryList = [...new Set(assetList.map(asset => asset.category))];
		const hasCategories = categoryList.length > 0 && !!categories;

		return (
			<div ref={ref => this.$assets = ref} class={s.assets} style={{ paddingTop: paddingTop + 'px' }}>
				{!hasCategories && (
					<div class={joinClasses(s.section, s.noCategories)}>
						<div class={s.sectionAssets}>
							{assetList.map(this.renderAsset)}
						</div>
					</div>
				)}
				{hasCategories && categoryList.map(category => (
					this.renderCategory(assetList, category)
				))}
			</div>
		);
	}

	renderCategory(assets, category) {
		const pageData = this.getPageData();
		const { categories = {} } = pageData;
		const { activeCategory } = this.state;
		const assetList = assets.filter(asset => asset.category === category);
		const showCategory = activeCategory === category || activeCategory === 'all';

		return (
			<div class={joinClasses(s.section, !showCategory && s.hide)} id={'category-' + category}>
				<div class={joinClasses(s.categoryHeader, 'smallB')}>
					<HTML markdown tag='div' class={s.title}>
						{(categories[category]) || category}
					</HTML>
					<div class={s.line} />
				</div>
				<div class={s.sectionAssets}>
					{assetList.map(this.renderAsset)}
				</div>
			</div>
		);
	}

	renderAsset(asset) {
		const { thumbnail, label, key, alt } = asset;
		const canShare = this.props.store?.pressKit?.canShareFile;
		const isDesktop = this.props.store?.client?.device === 'desktop';

		return (
			<div class={s.asset} ref={ref => this.$lazyLoadImages[key] = ref}>
				<img
					data-src={thumbnail}
					alt={alt}
					key={key}
				/>
				<div class={s.info}>
					<HTML tag='div' markdown class={joinClasses(s.label, 'small')}>
						{label}
					</HTML>
					<div class={s.icons}>
						{(canShare && !isDesktop && !asset.assetLink) && <Share class={s.icon} onClick={() => this.shareImage(asset)} />}
						<Download class={s.icon} onClick={() => this.downloadImage(asset)} />
					</div>
				</div>
			</div>
		);
	}

	render(props) {
		const appData = props.store?.appData || {};
		const pressKitStore = props.store?.pressKit || {};
		const pageData = this.getPageData();

		const client = props.store.client || {};
		const copy = appData.copy || {};
		const footerBgColorFallback = appData.styling?.variables?.tertiaryColor;
		const scrollElem = this.$scroller;

		const cookies = accessNested(props, 'store.cookies') || {};
		const sonyHeaderEnabled = accessNested(appData, 'options.sonyFeatures.sonyHeader', false);
		const externalBannerOpen = accessNested(props, 'store.externalCookieBanner.bannerOpen');
		const cookieMode = externalBannerOpen || (accessNested(cookies, 'pushDown', false) && accessNested(cookies, 'cookieBannerOpen', false));
		let style = {};
		let cookieHeight = 0;
		let containerStyle = {};
		if (cookieMode) {
			cookieHeight = externalBannerOpen ? accessNested(props, 'store.externalCookieBanner.height') : accessNested(cookies, 'height');
			style.top = cookieHeight + 'px';
			style.height = 'calc(var(--windowHeight) - ' + cookieHeight + 'px)';

			if (client.isDesktopSize) {
				containerStyle.left = ((window.innerHeight - cookieHeight - (sonyHeaderEnabled ? 35 : 0)) / 900 * 609) + 'px';
			}
		}

		containerStyle.pointerEvents = props.contentDisabled ? 'none' : 'all';

		return (
			<div class={s.wrapper}>
				<MovieDetails
					type="pressKit"
					tt={pageData.tt}
					bg={pageData.bg}
					buttons={pageData.buttons}
					visible={pressKitStore.show}
					copy={copy}
					appData={appData}
					cookieHeight={cookieHeight}
					cookieBannerOpen={cookieMode}
					key="presskitMovieDetails"
					isWidget={props.isWidget}
				/>
				<div
					class={joinClasses({
						s,
						container: true,
						animate: pressKitStore.animate,
						displayed: pressKitStore.show,
						loading: pressKitStore.loading,
						sonyHeader: sonyHeaderEnabled
					})}
					style={containerStyle}
					ref={ref => this.$container = ref}
				>
					<div
						id="pressKitScroller"
						class={s.scroller}
						data-section="scroller"
						ref={e => this.$scroller = e}
						onClick={this.scrollerClick}
						onScroll={this.onScrollerScroll}
					>
						<div
							class={joinClasses({ s, content: true })}
							ref={ref => this.$content = ref}
						>
							{this.renderHeader()}
							{this.renderAssets()}
							<Footer
								data={appData.footer}
								parent="pressKit"
								scrollElem={scrollElem}
								fallBackColor={footerBgColorFallback}
								key="footer"
								store={this.props.store}
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}
}